import { useEffect, useState } from "react";
import {
  deleteInvoiceApprovalMatrixRow,
  fetchInvoiceApprovalMatrix,
  fetchInvoiceNonPOConditions,
  importInvoiceApprovalMatrix,
  saveInvoiceApprovalMatrixRow,
} from "../api";

const MATCH_MODES = ["INCLUDE", "EXCLUDE", "ANY"];
const APPROVER_FIELDS = ["approver1", "approver2", "approver3", "approver4", "approver5", "approver6"];
const APPROVER_OPTIONS = [
  { value: "", label: "--Select--" },
  { value: "nasim@gmail.com", label: "Nasim" },
  { value: "chandan@gmail.com", label: "Chandan" },
  { value: "nitin@gmail.com", label: "Nitin" },
  { value: "gaurav@gmail.com", label: "Gaurav" },
  { value: "mayank@gmail.com", label: "Mayank" },
];

function createDraftRow(conditions) {
  return {
    conditions: Object.fromEntries(conditions.map(({ key }) => [key, { mode: "INCLUDE", values: [] }])),
    ...Object.fromEntries(APPROVER_FIELDS.map((field) => [field, ""])),
  };
}

function formatConditionCell(cell) {
  if (!cell || cell.mode === "ANY") return "ANY";
  const values = Array.isArray(cell.values) ? cell.values : (cell.value ? [cell.value] : []);
  return `${cell.mode}${values.length ? `: ${values.join(", ")}` : ""}`;
}

export default function InvoiceApprovalMatrixNonPOView() {
  const [rows, setRows] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [draft, setDraft] = useState(createDraftRow([]));
  const [loading, setLoading] = useState(true);
  const [conditionsLoading, setConditionsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  async function loadRows() {
    setLoading(true);
    try {
      setRows(await fetchInvoiceApprovalMatrix("non_po"));
    } catch (error) {
      setErrorMessage(error.message || "Unable to load Non-PO approval matrix.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadRows(); }, []);

  useEffect(() => {
    let active = true;
    fetchInvoiceNonPOConditions()
      .then((data) => {
        if (!active) return;
        const definitions = (Array.isArray(data) ? data : data?.conditions || [])
          .slice()
          .sort((left, right) => left.displayOrder - right.displayOrder)
          .map((condition) => ({
            key: condition.conditionKey,
            label: condition.subConditionName || condition.conditionKey,
            values: Array.isArray(condition.availableValues) ? condition.availableValues : [],
          }));
        setConditions(definitions);
        setDraft(createDraftRow(definitions));
      })
      .catch((error) => { if (active) setErrorMessage(error.message || "Unable to load configured Non-PO conditions."); })
      .finally(() => { if (active) setConditionsLoading(false); });
    return () => { active = false; };
  }, []);

  function updateCondition(key, field, value) {
    setDraft((current) => ({
      ...current,
      conditions: { ...current.conditions, [key]: { ...current.conditions[key], [field]: value } },
    }));
  }

  async function submit() {
    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      const response = await saveInvoiceApprovalMatrixRow({ matrix_type: "non_po", ...draft });
      setRows(Array.isArray(response?.data) ? response.data : [...rows, response.row]);
      setDraft(createDraftRow(conditions));
      setStatusMessage(response?.message || "Non-PO approval matrix row saved successfully.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to save Non-PO approval matrix row.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(rowId) {
    setStatusMessage("");
    setErrorMessage("");
    try {
      const response = await deleteInvoiceApprovalMatrixRow(rowId);
      setRows(Array.isArray(response?.data) ? response.data : rows.filter((row) => row.id !== rowId));
      setStatusMessage(response?.message || "Non-PO approval matrix row deleted successfully.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to delete Non-PO approval matrix row.");
    }
  }

  async function upload(event) {
    const [file] = event.target.files || [];
    if (!file) return;
    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      const response = await importInvoiceApprovalMatrix("non_po", file);
      setRows(Array.isArray(response?.data) ? response.data : rows);
      setStatusMessage(response?.message || "Path table imported successfully.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to import Non-PO path table.");
    } finally {
      setSaving(false);
      event.target.value = "";
    }
  }

  return (
    <section className="conditions-view conditions-view--approval-matrix">
      <div className="workspace-panel conditions-view__hero"><div>
        <h2>Approval Matrix_Non-PO</h2>
        <p className="config-view__intro">Non-PO invoice approval path setup.</p>
      </div></div>
      <div className="workspace-panel conditions-panel">
        <div className="users-form__header"><div><p className="eyebrow">Non-PO Invoices</p><h2>Path Table</h2></div></div>
        {statusMessage ? <div className="config-banner config-banner--success">{statusMessage}</div> : null}
        {errorMessage ? <div className="config-banner config-banner--error">{errorMessage}</div> : null}
        {!conditionsLoading && !conditions.length ? <div className="config-note">Add Non-PO conditions in Conditions_Non-PO before configuring the approval path table.</div> : null}
        <div className="config-table conditions-table-wrap"><table>
          <thead><tr><th>id</th>
            {conditions.map((condition) => <th key={condition.key}>{condition.label}</th>)}
            {APPROVER_FIELDS.map((field, index) => <th key={field}>{`approver${index + 1}`}</th>)}<th>Action</th>
          </tr></thead>
          <tbody>
            {loading || conditionsLoading ? <tr><td colSpan={conditions.length + APPROVER_FIELDS.length + 2}>Loading path table...</td></tr> : null}
            {!loading && !conditionsLoading && !rows.length ? <tr><td colSpan={conditions.length + APPROVER_FIELDS.length + 2}>No data available for this company. Add a row below or upload a path table.</td></tr> : null}
            {!loading && !conditionsLoading ? rows.map((row) => <tr key={row.id}>
              <td>{row.id}</td>
              {conditions.map((condition) => <td key={condition.key}>{formatConditionCell(row.conditions[condition.key])}</td>)}
              {APPROVER_FIELDS.map((field) => <td key={field}>{row[field] || "-"}</td>)}
              <td><button className="condition-icon-button condition-icon-button--danger" type="button" onClick={() => remove(row.id)}>Remove</button></td>
            </tr>) : null}
            <tr><td>{rows.length + 1}</td>
              {conditions.map((condition) => {
                const cell = draft.conditions[condition.key] || { mode: "INCLUDE", values: [] };
                return <td key={condition.key}><div className="condition-cell-stack">
                  <select value={cell.mode} onChange={(event) => updateCondition(condition.key, "mode", event.target.value)}>{MATCH_MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}</select>
                  <details className={`approval-matrix-values${cell.mode === "ANY" || !condition.values.length ? " is-disabled" : ""}`}>
                    <summary onClick={(event) => { if (cell.mode === "ANY" || !condition.values.length) event.preventDefault(); }}>
                      {cell.values.length ? cell.values.join(", ") : condition.values.length ? "--Select--" : "No values configured"}
                    </summary>
                    {condition.values.length ? <div className="approval-matrix-values__menu">{condition.values.map((value) => {
                      const checked = cell.values.includes(value);
                      return <label key={value}><input type="checkbox" checked={checked} disabled={cell.mode === "ANY"} onChange={() => updateCondition(condition.key, "values", checked ? cell.values.filter((item) => item !== value) : [...cell.values, value])} /><span>{value}</span></label>;
                    })}</div> : null}
                  </details>
                </div></td>;
              })}
              {APPROVER_FIELDS.map((field) => <td key={field}><select value={draft[field]} onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}>{APPROVER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></td>)}
              <td><button className="primary-button" type="button" onClick={submit} disabled={saving || !conditions.length}>{saving ? "Saving..." : "Submit"}</button></td>
            </tr>
          </tbody>
        </table></div>
        <div className="condition-actions-bar">
          <label className="ghost-button" htmlFor="invoice-path-upload-non-po">Upload Path Table</label>
          <input id="invoice-path-upload-non-po" type="file" accept=".xlsx,.xls,.csv" onChange={upload} hidden />
        </div>
      </div>
    </section>
  );
}
